/** 格式化工具函数 */

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

/** 格式化金额 */
export function formatCurrency(amount: number, currency: string = 'USD'): string {
  const currencyMap: Record<string, { locale: string; symbol: string }> = {
    USD: { locale: 'en-US', symbol: '$' },
    CNY: { locale: 'zh-CN', symbol: '¥' },
    EUR: { locale: 'de-DE', symbol: '€' },
    GBP: { locale: 'en-GB', symbol: '£' },
  };
  const cfg = currencyMap[currency] ?? currencyMap.USD;
  return `${cfg.symbol}${amount.toLocaleString(cfg.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** 紧凑金额（用于头图统计：$1.02M / $980K） */
export function formatCompactCurrency(amount: number, currency: string = 'USD'): string {
  const symbolMap: Record<string, string> = { USD: '$', CNY: '¥', EUR: '€', GBP: '£' };
  const symbol = symbolMap[currency] ?? '$';
  const abs = Math.abs(amount);
  if (abs >= 1_000_000) return `${symbol}${(amount / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) {
    const k = amount / 1_000;
    return `${symbol}${Number.isInteger(k) ? k : k.toFixed(1)}K`;
  }
  return `${symbol}${amount}`;
}

/** 格式化百分比 */
export function formatPercentage(value: number, decimals: number = 1): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

/** 格式化日期 */
export function formatDate(date: string | Date, format: string = 'YYYY-MM-DD'): string {
  return dayjs(date).format(format);
}

/** 格式化日期时间 */
export function formatDateTime(date: string | Date, format: string = 'YYYY-MM-DD HH:mm'): string {
  return dayjs(date).format(format);
}

/**
 * 业务时间戳统一按东八区（Asia/Shanghai）格式化。
 * 后端 created_at / valid_until 等业务时间以 LocalDateTime 裸字符串存储（语义=东八区，无时区偏移），
 * 直接用 dayjs 解析会按浏览器本地时区解释导致漂移；这里强制以东八区墙钟解读，
 * 保证中国用户与外国买家看到的是同一业务时刻（展示层统一东八区）。
 */
export function formatDateTimeCst(
  date: string | Date | null | undefined,
  format: string = 'YYYY-MM-DD HH:mm:ss'
): string {
  if (!date) return '-';
  const d = dayjs.tz(dayjs(date).format('YYYY-MM-DD HH:mm:ss'), 'Asia/Shanghai');
  return d.isValid() ? d.format(format) : '-';
}

/** 格式化相对时间 */
export function formatRelativeTime(date: string | Date): string {
  const now = dayjs();
  const target = dayjs(date);
  const diffMinutes = now.diff(target, 'minute');
  if (diffMinutes < 1) return '刚刚';
  if (diffMinutes < 60) return `${diffMinutes}分钟前`;
  const diffHours = now.diff(target, 'hour');
  if (diffHours < 24) return `${diffHours}小时前`;
  const diffDays = now.diff(target, 'day');
  if (diffDays < 30) return `${diffDays}天前`;
  return formatDate(date);
}

/** 格式化数字（千分位） */
export function formatNumber(num: number): string {
  return num.toLocaleString('zh-CN');
}

/** 截断文本 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

/**
 * 查看者浏览器时区（IANA，如 Asia/Shanghai / America/New_York）。
 * IoT 所有时间戳统一以 UTC 存储，展示时按查看者本地时区转换，
 * 北京用户看到北京时间、外国买家看到其本国时间。
 */
export function viewerTimeZone(): string {
  try {
    return dayjs.tz.guess();
  } catch {
    return 'Asia/Shanghai';
  }
}

/**
 * 将 UTC 墙钟时间字符串按【查看者本地时区】格式化展示。
 * 入参 reading_at 在后端统一以 UTC 存储（无时区列，语义=UTC）。
 * @example formatUtcInViewerTz('2026-08-26 01:48:49') → 北京 '2026-08-26 09:48:49' / 纽约 '2026-08-25 21:48:49'
 */
export function formatUtcInViewerTz(
  utcStr: string | null | undefined,
  format: string = 'YYYY-MM-DD HH:mm:ss'
): string {
  if (!utcStr) return '—';
  const tz = viewerTimeZone();
  return dayjs.utc(utcStr).tz(tz).format(format);
}

/**
 * 时区标签，如 "UTC+8 北京" / "UTC-4 纽约"，用于界面标注避免歧义。
 */
export function viewerTzLabel(): string {
  const tz = viewerTimeZone();
  const offsetMin = dayjs().tz(tz).utcOffset();
  const sign = offsetMin >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMin);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const offStr = `UTC${sign}${h}${m ? ':' + String(m).padStart(2, '0') : ''}`;
  const cityMap: Record<string, string> = {
    'Asia/Shanghai': '北京',
    'Asia/Urumqi': '乌鲁木齐',
    'Asia/Tokyo': '东京',
    'Asia/Dubai': '迪拜',
    'Europe/London': '伦敦',
    'Europe/Paris': '巴黎',
    'America/New_York': '纽约',
    'America/Los_Angeles': '洛杉矶',
  };
  const city = cityMap[tz] ?? tz;
  return `${offStr} ${city}`;
}
